import React, { useRef, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import {
  DocumentIcon,
  FolderIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import * as ShareAPI from "../FileShareFeature/api"
import FileTree from "../FileShareFeature/FileTree"
import { formatBytes } from "./utils"
import { openGoPremium } from "../GoPremiumDialog"

const CHUNK_SIZE = 50 * 1024 * 1024 // 50 MB
const CHUNKED_THRESHOLD = 100 * 1024 * 1024

// Walk a dropped folder entry recursively into { file, path } records.
const readEntry = (entry, base = "") => new Promise((resolve) => {
  if (entry.isFile) {
    entry.file(
      (file) => resolve([{ file, path: base + entry.name }]),
      () => resolve([])
    )
  } else if (entry.isDirectory) {
    const reader = entry.createReader()
    const collected = []
    const readBatch = () => reader.readEntries(async (batch) => {
      if (!batch.length) {
        const nested = await Promise.all(collected.map((e) => readEntry(e, base + entry.name + "/")))
        resolve(nested.flat())
      } else {
        collected.push(...batch)
        readBatch()
      }
    }, () => resolve([]))
    readBatch()
  } else {
    resolve([])
  }
})

export default function AddMoreDialog({ isOpen, onClose, share, onAdded }) {
  const texts = TextBundle({ id: "transfer_share_dashboard" })
  const fileInputRef = useRef(null)
  const folderInputRef = useRef(null)
  const abortRef = useRef(null)

  const [entries, setEntries] = useState([]) // { file, path, key, progress, status }
  const [uploading, setUploading] = useState(false)
  const [completedCount, setCompletedCount] = useState(0)

  const reset = () => {
    setEntries([])
    setUploading(false)
    setCompletedCount(0)
  }

  const close = () => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = null
    reset()
    onClose?.()
  }

  const addEntries = (list) => {
    if (!list.length) return
    setEntries((prev) => {
      const existingKeys = new Set(prev.map((e) => `${e.path}|${e.file.size}`))
      const next = []
      for (const { file, path } of list) {
        const k = `${path}|${file.size}`
        if (existingKeys.has(k)) continue
        existingKeys.add(k)
        next.push({ file, path, key: `${path}-${file.size}-${Math.random().toString(36).slice(2, 8)}`, progress: 0, status: 'pending' })
      }
      return prev.concat(next)
    })
  }

  const handleFiles = (fileList) => {
    addEntries(Array.from(fileList || []).map((f) => ({ file: f, path: f.webkitRelativePath || f.name })))
  }

  const handleDrop = (e) => {
    e.preventDefault()
    if (uploading) return
    const items = e.dataTransfer?.items
    if (items?.length && items[0]?.webkitGetAsEntry) {
      const roots = []
      for (const it of items) {
        const en = it.webkitGetAsEntry?.()
        if (en) roots.push(en)
      }
      if (roots.length) {
        Promise.all(roots.map((en) => readEntry(en))).then((r) => addEntries(r.flat()))
        return
      }
    }
    if (e.dataTransfer?.files?.length) {
      addEntries(Array.from(e.dataTransfer.files).map((f) => ({ file: f, path: f.webkitRelativePath || f.name })))
    }
  }

  const removeEntry = (key) => {
    if (uploading) return
    setEntries((prev) => prev.filter((e) => e.key !== key))
  }

  // FileTree-compatible remove: by path (file) or path prefix (folder).
  const removeByPath = (path, isFolder) => {
    if (uploading) return
    const prefix = path + "/"
    setEntries((prev) => prev.filter((e) => (
      isFolder ? !(e.path === path || e.path.startsWith(prefix)) : e.path !== path
    )))
  }

  const updateEntry = (key, patch) => {
    setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ...patch } : e)))
  }

  const totalSize = entries.reduce((s, e) => s + e.file.size, 0)
  const canUpload = entries.length > 0 && !uploading

  const handleUpload = async () => {
    if (!share?.orderID || entries.length === 0) return

    // Pre-flight quota check — refuse before uploading a single byte.
    try {
      const quota = await ShareAPI.GetQuota()
      if (quota?.result) {
        const newSize = entries.reduce((s, e) => s + e.file.size, 0)
        const maxSize = quota.maxSize == null ? Infinity : quota.maxSize
        const volumeLimit = quota.volumeLimit == null ? Infinity : quota.volumeLimit
        const volumeUsed = quota.volumeUsed || 0
        const oversize = entries.find((e) => e.file.size > maxSize)
        if (oversize) {
          showMessageDialog({
            variant: 'warning',
            title: texts?.notice,
            message: `"${oversize.path}" ${texts?.exceedsPerFileLimit} ${formatBytes(maxSize)}.`,
            btnLabel: texts?.upgradeToPro,
            onConfirm: openGoPremium,
          })
          return
        }
        if (volumeUsed + newSize > volumeLimit) {
          showMessageDialog({
            variant: 'warning',
            title: texts?.notice,
            message: `${texts?.addingTheseFiles} (${formatBytes(volumeUsed + newSize)} / ${formatBytes(volumeLimit)})`,
            btnLabel: texts?.upgradeToPro,
            onConfirm: openGoPremium,
          })
          return
        }
      }
    } catch {
      // If the quota fetch fails, fall through — the server will still enforce.
    }

    setUploading(true)
    setCompletedCount(0)
    const abortController = new AbortController()
    abortRef.current = abortController

    try {
      // Sort by path so uploads run top-to-bottom in the same order the tree
      // displays files (case-insensitive, mirrors FileTree's alphabetical sort).
      const ordered = [...entries].sort((a, b) =>
        a.path.localeCompare(b.path, undefined, { sensitivity: 'base' })
      )
      for (const entry of ordered) {
        if (abortController.signal.aborted) throw new Error('aborted')
        updateEntry(entry.key, { status: 'uploading', progress: 0 })
        const { file, path: relPath } = entry
        try {
          if (file.size < CHUNKED_THRESHOLD) {
            await ShareAPI.UploadShareFile(
              share.orderID, file,
              (loaded) => updateEntry(entry.key, { progress: file.size > 0 ? loaded / file.size : 0 }),
              abortController.signal,
              relPath,
            )
          } else {
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE)
            for (let i = 0; i < totalChunks; i++) {
              if (abortController.signal.aborted) throw new Error('aborted')
              const start = i * CHUNK_SIZE
              const blob = file.slice(start, Math.min(start + CHUNK_SIZE, file.size))
              await ShareAPI.UploadShareChunk(
                share.orderID, i, totalChunks, relPath, file.size, blob,
                (chunkLoaded) => updateEntry(entry.key, { progress: file.size > 0 ? (start + chunkLoaded) / file.size : 0 }),
                abortController.signal,
              )
            }
            const finalResult = await ShareAPI.FinalizeShareUpload(share.orderID)
            if (finalResult?.error) throw new Error(finalResult.error)
          }
          updateEntry(entry.key, { status: 'done', progress: 1 })
          setCompletedCount((c) => c + 1)
        } catch (err) {
          updateEntry(entry.key, { status: 'error', error: err.message })
          throw err
        }
      }

      // Commit the just-uploaded files into the share manifest.
      const result = await ShareAPI.AppendShareFiles(share.orderID)
      if (result?.error) {
        const QUOTA_ERRORS = {
          file_too_large: texts?.fileTooLarge,
          volume_exceeded: texts?.volumeExceeded,
          no_files_to_add: texts?.noFilesToAdd,
        }
        const msg = QUOTA_ERRORS[result.error] || result.error
        showMessageDialog({
          title: texts?.uploadFailed,
          message: msg,
          buttons: result.error === 'volume_exceeded' || result.error === 'file_too_large'
            ? [{ label: texts?.upgradeToPro, onClick: openGoPremium, variant: 'primary' }, { label: texts?.cancel }]
            : undefined,
        })
        return
      }

      onAdded?.(result)
      reset()
      onClose?.()
    } catch (err) {
      if (err.message !== 'aborted') {
        showMessageDialog({
          title: texts?.uploadFailed,
          message: err.message || (texts?.tryAgain),
        })
      }
    } finally {
      setUploading(false)
      abortRef.current = null
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={texts?.addMoreFiles}
      variant="compact"
      className="max-w-xl"
      closeOnClickOutside={false}
      closeOnEscape={!uploading}
    >
      <div className="flex flex-col gap-4 p-5">
        {/* Add buttons */}
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="primary_s"
            onClick={() => { if (!uploading) fileInputRef.current?.click() }}
            disabled={uploading}
          >
            <DocumentIcon className="h-4 w-4" />
            {texts?.addFiles}
          </Button>
          <Button
            variant="primary_s"
            onClick={() => { if (!uploading) folderInputRef.current?.click() }}
            disabled={uploading}
          >
            <FolderIcon className="h-4 w-4" />
            {texts?.addFolder}
          </Button>
        </div>

        {/* Drop zone / file tree */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          {entries.length === 0 ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-bordered py-10 text-text_secondary hover:text-text hover:border-primary transition-colors"
            >
              <div className="flex items-center gap-2 text-primary">
                <DocumentIcon className="h-7 w-7" />
                <PlusIcon className="h-5 w-5" />
                <FolderIcon className="h-8 w-8" />
              </div>
              <span className="text-sm font-medium">{texts?.addFilesOrFolders}</span>
            </button>
          ) : (
            <FileTree
              entries={entries}
              onRemove={removeByPath}
              disabled={uploading}
              progressMap={Object.fromEntries(entries.map((e) => [e.path, e.progress]))}
              statusMap={Object.fromEntries(entries.map((e) => [e.path, e.status]))}
              maxHeightClass="max-h-[40vh]"
            />
          )}
        </div>

        {entries.length > 0 && (
          <Typography variant="txt2_dim" className="block text-right">
            {entries.length} {entries.length === 1 ? (texts?.file) : (texts?.files)} · {formatBytes(totalSize)}
          </Typography>
        )}

        {/* Footer: counter left, upload right */}
        <div className="flex items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-2">
            {uploading && <Spinner variant="primary-thin" className="w-4 h-4" />}
            <Typography variant="txt2_dim" className="block">
              {completedCount} / {entries.length} {texts?.uploaded}
            </Typography>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={close}
              disabled={uploading}
            >
              {texts?.cancel}
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!canUpload}
              className="min-w-[120px]"
            >
              {uploading && <Spinner variant="minimal" />}
              {texts?.upload}
            </Button>
          </div>
        </div>

        <div className="hidden">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = '' }}
          />
          <input
            ref={folderInputRef}
            type="file"
            webkitdirectory=""
            directory=""
            multiple
            onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = '' }}
          />
        </div>
      </div>
    </Modal>
  )
}
